import {changeAgentLicenseFilters, FilterEnum, getAgentLicenses} from 'Agent/AgentInformation/ducks/agents';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import {
  withDateFilterProperties,
  withSelectFilterProperties,
  withStringFilterProperties,
} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {toFormattedDate} from 'Shared/utilities/DateFormatter';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {selectCurrentAgentNumbers} from 'Users/selectors/userProfileSelectors';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import FileDownloadFab from 'Shared/components/FileDownloadFab';
import {prop} from 'Shared/utilities/prop';
import {useMediaQuery} from '@material-ui/core';
import {theme} from 'Core/components/WithTheme';

type AgencyLicensesProps = {
  selectedAgency: AgencyFilters;
} & RouteComponentProps;

const AgentLicenses = ({match, selectedAgency}: AgencyLicensesProps) => {
  const dispatch = useDispatch();
  useBreadcrumbs('Agent Licenses', match.url);
  const {agentLicenses, filters} = useSelector((state: ReducerState) => state.agents);
  const agentNumbers = useSelector((state: ReducerState) => selectCurrentAgentNumbers(state));

  const changeFilter = withValueChangedHook(filters, changeAgentLicenseFilters, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: filters,
  };

  const statuses: FilterEnum[] = [
    {
      name: 'Active',
      value: 'Active',
    },
    {
      name: 'Inactive',
      value: 'Inactive',
    },
  ];

  const ProductAvailability = {
    Y: 'Yes',
    N: 'No',
  };

  const toProductAvailbility = (value: boolean) => (value ? ProductAvailability.Y : ProductAvailability.N);
  const {currentAgentNumbers} = useSelector((state: ReducerState) => state.user.profile);

  useEffect(() => {
    dispatch(changeAgentLicenseFilters(filters));
  }, [agentNumbers, dispatch, filters]);

  useEffect(() => {
    dispatch(
      getAgentLicenses(
        currentAgentNumbers,
        selectedAgency.isNewYork,
        selectedAgency.encryptedIdentifier,
        undefined,
        filters
      )
    );
  }, [currentAgentNumbers, dispatch, filters, selectedAgency.isNewYork, selectedAgency.encryptedIdentifier]);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const exportToCsvProps = {
    startIcon: <i className="fa fa-2x fa-file-download" />,
    extension: FileExtension.CSV,
    downloadedFileName: 'FilteredAgentLicenses',
    relativeFileLocation: `api/agent/v1/licenses/csv${addFilterParameters({
      ...filters,
      agentNumbers: agentNumbers.length > 0 ? agentNumbers.map(prop('value')) : 'value',
      isNewYork: selectedAgency.isNewYork,
      encryptedIdentifier: selectedAgency.encryptedIdentifier,
    })}`,
    children: 'Export To CSV',
  };

  return (
    <PagedView
      pagedViewKey="agentLicenses"
      data={agentLicenses}
      loadMore={page =>
        dispatch(
          getAgentLicenses(
            currentAgentNumbers,
            selectedAgency.isNewYork,
            selectedAgency.encryptedIdentifier,
            page,
            filters
          )
        )
      }
      actions={
        isMobile ? (
          <FileDownloadFab variant="extended" color="primary" displayTooltip {...exportToCsvProps} />
        ) : (
          <FileDownloadButton variant="contained" color="primary" displayTooltip {...exportToCsvProps} />
        )
      }
    >
      {columnProps => (
        <>
          <Column
            {...columnProps}
            title="Agent Name"
            filterProperties={withStringFilterProperties(filterProperties, 'agentName')}
            default
          >
            {agentLicense => agentLicense.agentName}
          </Column>
          <Column
            {...columnProps}
            title="Agent Number"
            filterProperties={withStringFilterProperties(filterProperties, 'agentNumber')}
            default
          >
            {agentLicense => agentLicense.agentNumber}
          </Column>
          <Column
            {...columnProps}
            title="Manager Name"
            filterProperties={withStringFilterProperties(filterProperties, 'managerName')}
            default
          >
            {agentLicense => agentLicense.managerName}
          </Column>
          <Column
            {...columnProps}
            title="Manager Number"
            filterProperties={withStringFilterProperties(filterProperties, 'managerNumber')}
            default
          >
            {agentLicense => agentLicense.managerNumber}
          </Column>
          <Column
            {...columnProps}
            title="License Number"
            filterProperties={withStringFilterProperties(filterProperties, 'licenseNumber')}
            default
          >
            {agentLicense => agentLicense.licenseNumber}
          </Column>
          <Column
            {...columnProps}
            title="State"
            filterProperties={withStringFilterProperties(filterProperties, 'licenseState')}
            default
          >
            {agentLicense => agentLicense.licenseState}
          </Column>
          <Column
            {...columnProps}
            title="Status"
            filterProperties={withSelectFilterProperties(filterProperties, 'status', statuses)}
            default
          >
            {agentLicense => agentLicense.status}
          </Column>
          <Column
            {...columnProps}
            title="Effective Date"
            filterProperties={withDateFilterProperties(filterProperties, 'effectiveDate')}
            default
          >
            {agentLicense => toFormattedDate(agentLicense.effectiveDate!)}
          </Column>
          <Column
            {...columnProps}
            title="Expiration Date"
            filterProperties={withDateFilterProperties(filterProperties, 'expirationDate')}
            default
          >
            {agentLicense => toFormattedDate(agentLicense.expirationDate!)}
          </Column>
          <Column {...columnProps} title="Life">
            {agentLicense => toProductAvailbility(agentLicense.life!)}
          </Column>
          <Column {...columnProps} title="Deferred Annuity">
            {agentLicense => toProductAvailbility(agentLicense.deferredAnnuity!)}
          </Column>
          <Column {...columnProps} title="ADD">
            {agentLicense => toProductAvailbility(agentLicense.additional!)}
          </Column>
          <Column {...columnProps} title="Dis">
            {agentLicense => toProductAvailbility(agentLicense.disability!)}
          </Column>
          <Column {...columnProps} title="Med">
            {agentLicense => toProductAvailbility(agentLicense.medical!)}
          </Column>
          <Column {...columnProps} title="UL">
            {agentLicense => toProductAvailbility(agentLicense.universalLife!)}
          </Column>
        </>
      )}
    </PagedView>
  );
};

export default AgentLicenses;
